/**
 * Personal director client (docs/personal-director-api.md). Lives beside `api.ts` and is exposed
 * as `client.director` by the HTTP client only: the dev fixture driver has no director, and the UI
 * hides director entry points when it is missing.
 *
 * Contract rules kept here, in one place:
 *  - API-returned media URLs (speech segments, take media) are API-relative → prefix the base.
 *  - `sharePath` (`/story/:id`) is a consumer route → NEVER prefixed.
 *  - 429 is surfaced as `DirectorBusyError` so screens can show a designed "busy" state.
 */
import type { DirectorAudio, DirectorError, DirectorErrorCode, DirectorLanguage, DirectorProfile, DirectorResponse, DirectorShow, DirectorStyle, DirectorVoice, PersonalStory, CallLock, Receipt } from '@clutch/core';

export type OnboardingQuestion = {
  id: 'interest' | 'language' | 'familiarity' | string;
  prompt: string;
  fields: string[];
  skippable: boolean;
  options: { value: string; label: string }[];
  skipPatch: Partial<DirectorProfile>;
};
export type DirectorOnboarding = {
  profile: DirectorProfile;
  suggestedLanguage: DirectorLanguage;
  /** False only before the fan has explicitly chosen a language. */
  languageAnswered?: boolean;
  ready: boolean;
  nextQuestion: OnboardingQuestion | null;
};
export type HistoryParticipation = {
  roomId: string;
  gameId: string;
  home: string;
  away: string;
  asOfSeq: number;
  /** Latest observed activity by this fan; absent when legacy activity time is unknown. */
  lastActivityAt?: string;
  call?: CallLock;
  receipt?: Receipt;
  takeIds: string[];
};
export type DirectorHistory = {
  memory: { roomId: string; gameId: string; seenThroughSeq: number; at: string }[];
  participation: HistoryParticipation[];
  stories: { storyId: string; publishedAt: string; game: DirectorResponse['game']; sharePath: string }[];
};
export type DirectorCapabilities = {
  plannerConfigured: boolean;
  narratorConfigured?: boolean;
  narratorModel?: string;
  speechConfigured: boolean;
  speechProvider?: string;
  speechModel?: string;
  languages: DirectorLanguage[];
  /** Locales verified/enabled for the strict model + speech pathway. */
  locales?: DirectorLanguage[];
  /** Show styles the planner/narrator honours (absent on older servers). */
  styles?: DirectorStyle[];
  /** Whether (or how many) catalog voices can be chosen; absent on older servers. */
  voicesAvailable?: boolean | number;
  plannerModel?: string;
  /** 'model' = an LLM writes each show in its style; 'template' = verified factual templates only. */
  scriptMode?: 'model' | 'template';
  defaultVoiceId?: string;
  voiceCatalog?: 'ok' | 'unavailable' | 'not_configured';
  /** Server configuration keys that are absent (e.g. OPENROUTER_API_KEY, ELEVENLABS_API_KEY). */
  missing?: string[];
  maxMoments?: number;
};

export class DirectorBusyError extends Error {}
export class DirectorHttpError extends Error {
  constructor(readonly status: number, message: string, readonly body?: unknown) {
    super(message);
  }
}

const DIRECTOR_CODES: readonly DirectorErrorCode[] = ['AI_DIRECTOR_UNAVAILABLE', 'DIRECTOR_VALIDATION_FAILED', 'AUDIO_UNAVAILABLE', 'UNSUPPORTED_LOCALE'];
const strs = (v: unknown) => Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()).slice(0, 12) : undefined;
/** A typed Director failure (canonical spec rule 3). The flagship path renders it; it never downgrades. */
export class DirectorUnavailableError extends Error {
  constructor(readonly detail: DirectorError) {
    super(detail.message);
  }
}
/** Read a typed error from any server body shape: `{code,…}`, `{error:{code,…}}` or `{error:'…', code}`. */
export function directorErrorOf(body: unknown): DirectorError | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const o = body as Record<string, unknown>;
  const inner = o.error && typeof o.error === 'object' ? o.error as Record<string, unknown> : o;
  const code = (inner.code ?? o.code) as DirectorErrorCode;
  if (!DIRECTOR_CODES.includes(code)) return undefined;
  const message = typeof inner.message === 'string' ? inner.message : typeof o.message === 'string' ? o.message : typeof o.error === 'string' ? o.error : code;
  const missing = strs(inner.missing ?? o.missing);
  const reasons = strs(inner.reasons ?? o.reasons);
  return { code, message, retriable: (inner.retriable ?? o.retriable) !== false, ...(missing?.length ? { missing } : {}), ...(reasons?.length ? { reasons } : {}) };
}
/**
 * The strict flagship contract, enforced on the client too: an older server that ignores
 * `require:'model'` and answers with a rules/template story is still "no AI Director".
 */
export function requireModelShow(d: DirectorResponse): DirectorResponse {
  // The AI writer authors the show; moments may come from the engine when the planner is down.
  const authored = d.narration?.mode === 'model' && (d.generation?.mode === 'model' || d.generation?.reason === 'planner_failed_engine_moments');
  if (!authored) throw new DirectorUnavailableError({ code: 'AI_DIRECTOR_UNAVAILABLE', message: 'The AI Director did not author this show.', retriable: true, ...(d.generation?.reason ? { reasons: [d.generation.reason] } : {}) });
  if (!d.showPlan?.factualValidation?.valid) {
    throw new DirectorUnavailableError({ code: 'DIRECTOR_VALIDATION_FAILED', message: 'The show did not pass the fact check.', retriable: true, reasons: d.showPlan?.factualValidation?.errors?.slice(0, 6) ?? ['missing_validated_show_plan'] });
  }
  return d;
}

/** Dev inspector provenance (`GET /director/:id/inspector`), feature-detected. */
export type DirectorInspection = {
  promptVersion?: string; model?: string; provider?: string; requestIds?: string[]; latencyMs?: number; attempts?: number;
  validation?: string[]; voiceId?: string; voice?: string; synthesisMs?: number; cache?: string | boolean; sourceFreshness?: string;
  chapters?: { segmentId: string; eventIds?: string[]; decision?: string }[];
  [k: string]: unknown;
};

export interface DirectorApi {
  readonly base: string;
  capabilities(): Promise<DirectorCapabilities | undefined>;
  profile(fanId: string): Promise<DirectorProfile>;
  saveProfile(fanId: string, patch: Partial<DirectorProfile>): Promise<DirectorProfile>;
  onboarding(fanId: string, locale: string): Promise<DirectorOnboarding>;
  history(fanId: string): Promise<DirectorHistory>;
  catchUp(roomId: string, body: { fanId: string; language?: DirectorLanguage; asOfSeq?: number; maxMoments?: number; show?: Partial<DirectorShow>; require?: 'model' }): Promise<DirectorResponse>;
  /** `GET /director/voices/:id/sample?language=` — the same verified line per voice; undefined on 404 (older server). */
  voiceSample?(voiceId: string, language: DirectorLanguage): Promise<string | undefined>;
  /** `GET /director/:id/inspector`; undefined when the server has none. */
  inspector?(directorId: string, fanId: string): Promise<DirectorInspection | undefined>;
  speech(directorId: string, fanId: string, voiceId?: string): Promise<DirectorAudio>;
  /** `GET /director/voices`; undefined when the server has no catalog (404/older server). */
  voices?(): Promise<DirectorVoice[] | undefined>;
  seen(roomId: string, fanId: string, asOfSeq: number): Promise<void>;
  publish(body: { fanId: string; directorId: string; takeId?: string }): Promise<PersonalStory>;
  story(storyId: string, ownerFanId?: string): Promise<PersonalStory | undefined>;
  storySpeech(storyId: string, ownerFanId?: string): Promise<DirectorAudio | undefined>;
  publishSpeech(storyId: string, fanId: string): Promise<DirectorAudio>;
  removeStory(storyId: string, fanId: string): Promise<void>;
  /** API-relative media URL → fetchable URL. */
  media(url: string): string;
}

/** API-relative → app URL. Absolute/blob/data URLs and already-prefixed paths are kept. */
export function apiMediaUrl(base: string, url: string): string {
  if (/^(https?:|blob:|data:)/.test(url)) return url;
  if (url.startsWith(`${base}/`)) return url;
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** The public link for a story. `sharePath` is consumer-relative: never `/api`. */
export function storyShareUrl(origin: string, story: Pick<PersonalStory, 'sharePath' | 'storyId'>): string {
  const path = story.sharePath?.startsWith('/story/') ? story.sharePath : `/story/${encodeURIComponent(story.storyId)}`;
  return `${origin}${path}`;
}

/** Owner-only inspector body (`{inspector, validation, evidence, …}`) or a flat shape → one view. */
export function inspectionOf(body: Record<string, unknown> | undefined): DirectorInspection | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const nested = body.inspector && typeof body.inspector === 'object' ? body.inspector as DirectorInspection : undefined;
  const validation = body.validation && typeof body.validation === 'object' && !Array.isArray(body.validation) ? (body.validation as { outcome?: string[] }).outcome : undefined;
  const segments = (body.evidence as { segments?: { segmentId: string; eventIds?: string[] }[] } | undefined)?.segments;
  const { inspector: _i, validation: _v, evidence: _e, ...rest } = body;
  void _i; void _v; void _e;
  return {
    ...(rest as DirectorInspection), ...(nested ?? {}),
    ...(validation ? { validation } : Array.isArray(body.validation) ? { validation: body.validation as string[] } : {}),
    ...(segments ? { chapters: segments.map((s) => ({ segmentId: s.segmentId, eventIds: s.eventIds ?? [] })) } : {}),
  };
}

export function createDirectorApi(base = '/api', f: typeof fetch = (...a) => fetch(...a)): DirectorApi {
  const enc = encodeURIComponent;
  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await f(`${base}${path}`, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = undefined;
    }
    if (res.status === 429) throw new DirectorBusyError((parsed as { error?: string } | undefined)?.error ?? 'busy');
    const typed = res.ok ? undefined : directorErrorOf(parsed);
    if (typed) throw new DirectorUnavailableError(typed);
    if (res.status === 400 && (parsed as { error?: string } | undefined)?.error === 'invalid_voice') {
      throw new DirectorUnavailableError({ code: 'AUDIO_UNAVAILABLE', message: 'The selected voice is no longer available. Choose another voice.', retriable: true, reasons: ['selected_voice_unavailable'] });
    }
    if (!res.ok) {
      const err = (parsed as { error?: unknown } | undefined)?.error;
      throw new DirectorHttpError(res.status, typeof err === 'string' ? err : `${method} ${path} → ${res.status}`, parsed);
    }
    return parsed as T;
  }
  return {
    base,
    capabilities: () => req<DirectorCapabilities>('GET', '/director/capabilities').catch(() => undefined),
    profile: (fanId) => req('GET', `/fans/${enc(fanId)}/director-profile`),
    saveProfile: (fanId, patch) => req('PATCH', `/fans/${enc(fanId)}/director-profile`, patch),
    onboarding: (fanId, locale) => req('GET', `/fans/${enc(fanId)}/director-onboarding?locale=${enc(locale)}`),
    history: (fanId) => req('GET', `/fans/${enc(fanId)}/director-history`),
    catchUp: (roomId, body) => req('POST', `/rooms/${enc(roomId)}/director`, body),
    speech: (directorId, fanId, voiceId) => req('POST', `/director/${enc(directorId)}/speech`, voiceId ? { fanId, voiceId } : { fanId }),
    voiceSample: async (voiceId, language) => {
      const res = await f(`${base}/director/voices/${enc(voiceId)}/sample?language=${enc(language)}`);
      if (res.status === 404) return undefined;
      if (!res.ok) {
        let body: unknown;
        try { body = await res.json(); } catch { body = undefined; }
        throw new DirectorUnavailableError(directorErrorOf(body) ?? { code: 'AUDIO_UNAVAILABLE', message: `Voice sample → ${res.status}`, retriable: true });
      }
      return URL.createObjectURL(await res.blob());
    },
    inspector: (directorId, fanId) => req<Record<string, unknown>>('GET', `/director/${enc(directorId)}/inspector?fanId=${enc(fanId)}`).then(inspectionOf).catch(() => undefined),
    voices: () => req<DirectorVoice[] | { voices: DirectorVoice[] }>('GET', '/director/voices')
      .then((v) => (Array.isArray(v) ? v : Array.isArray(v?.voices) ? v.voices : undefined)).catch(() => undefined),
    seen: async (roomId, fanId, asOfSeq) => {
      await req('POST', `/rooms/${enc(roomId)}/director/seen`, { fanId, asOfSeq });
    },
    publish: (body) => req('POST', '/stories', body),
    story: async (storyId, ownerFanId) => {
      try {
        return await req<PersonalStory>('GET', `/stories/${enc(storyId)}${ownerFanId ? `?fanId=${enc(ownerFanId)}` : ''}`);
      } catch (e) {
        if (e instanceof DirectorHttpError && e.status === 404) return undefined;
        throw e;
      }
    },
    storySpeech: (storyId, ownerFanId) => req<DirectorAudio>('GET', `/stories/${enc(storyId)}/speech${ownerFanId ? `?fanId=${enc(ownerFanId)}` : ''}`).catch(() => undefined),
    publishSpeech: (storyId, fanId) => req<DirectorAudio>('POST', `/stories/${enc(storyId)}/speech`, { fanId }),
    removeStory: async (storyId, fanId) => {
      await req('DELETE', `/stories/${enc(storyId)}?fanId=${enc(fanId)}`);
    },
    media: (url) => apiMediaUrl(base, url),
  };
}
